/**
 * Components Schema
 * Custom component library and versioning tables
 */

import { pgTable, uuid, varchar, text, timestamp, boolean, integer } from 'drizzle-orm/pg-core';
import { relations } from 'drizzle-orm';
import { profiles } from './profiles';

// =============================================================================
// Component Categories
// =============================================================================
export const componentCategories = pgTable('component_categories', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  slug: varchar('slug', { length: 255 }).notNull().unique(),
  description: text('description'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Custom Components
// =============================================================================
export const customComponents = pgTable('custom_components', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  description: text('description'),
  content: text('content').notNull().default('{}'), // JSON
  categoryId: uuid('category_id'),
  createdBy: uuid('created_by').notNull(),
  isPublic: boolean('is_public').default(false),
  version: integer('version').default(1),
  tags: text('tags'), // JSON array stored as text
  accessControl: text('access_control'), // JSON
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// =============================================================================
// Component Versions
// =============================================================================
export const componentVersions = pgTable('component_versions', {
  id: uuid('id').primaryKey().defaultRandom(),
  componentId: uuid('component_id').notNull(),
  name: varchar('name', { length: 255 }).notNull(),
  description: text('description'),
  content: text('content').notNull().default('{}'), // JSON
  versionNumber: integer('version_number').notNull(),
  createdBy: uuid('created_by').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Design Templates
// =============================================================================
export const designTemplates = pgTable('design_templates', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  description: text('description'),
  category: varchar('category', { length: 100 }),
  content: text('content').default('{}'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Relations
// =============================================================================
export const componentCategoriesRelations = relations(componentCategories, ({ many }) => ({
  components: many(customComponents),
}));

export const customComponentsRelations = relations(customComponents, ({ one, many }) => ({
  category: one(componentCategories, {
    fields: [customComponents.categoryId],
    references: [componentCategories.id],
  }),
  createdByUser: one(profiles, {
    fields: [customComponents.createdBy],
    references: [profiles.id],
  }),
  versions: many(componentVersions),
}));

export const componentVersionsRelations = relations(componentVersions, ({ one }) => ({
  component: one(customComponents, {
    fields: [componentVersions.componentId],
    references: [customComponents.id],
  }),
  createdByUser: one(profiles, {
    fields: [componentVersions.createdBy],
    references: [profiles.id],
  }),
}));

// =============================================================================
// Types
// =============================================================================
export type ComponentCategory = typeof componentCategories.$inferSelect;
export type NewComponentCategory = typeof componentCategories.$inferInsert;
export type CustomComponent = typeof customComponents.$inferSelect;
export type NewCustomComponent = typeof customComponents.$inferInsert;
export type ComponentVersion = typeof componentVersions.$inferSelect;
export type NewComponentVersion = typeof componentVersions.$inferInsert;
export type DesignTemplate = typeof designTemplates.$inferSelect;
export type NewDesignTemplate = typeof designTemplates.$inferInsert;
